import React, { useState } from 'react';
import { authFetch } from '../../context/AuthContext';
import ProductSearch, { StockProduct } from '../../components/ui/ProductSearch';

interface Item {
  product_id?: number;
  product_name: string;
  unit_price: string;
  quantity: string;
  stock?: number;   // 현재 재고 (표시용)
}

interface Props { onClose: () => void; onSuccess: (msg: string) => void; }

const emptyItem = (): Item => ({ product_name: '', unit_price: '', quantity: '' });

export default function ProductShipmentModal({ onClose, onSuccess }: Props) {
  const [clientName,   setClientName]   = useState('');
  const [shipmentDate, setShipmentDate] = useState(new Date().toISOString().split('T')[0]);
  const [items,        setItems]        = useState<Item[]>([emptyItem()]);
  const [note,         setNote]         = useState('');
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');

  const total = items.reduce((s, i) =>
    s + (parseInt(i.quantity) || 0) * (parseInt(i.unit_price) || 0), 0);

  const setItem = (idx: number, patch: Partial<Item>) =>
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));

  const removeItem = (idx: number) =>
    setItems(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : [emptyItem()]);

  /* 제품 선택 시 자동 채우기 */
  const onProductSelect = (idx: number, p: StockProduct) => {
    setItem(idx, {
      product_id:   p.id,
      product_name: p.name,
      unit_price:   String(p.price),
      stock:        p.stock,
    });
  };

  /* 재고 초과 검증 */
  const stockWarn = (item: Item): string | null => {
    if (item.stock === undefined || !item.quantity) return null;
    const qty = parseInt(item.quantity) || 0;
    if (qty <= 0) return null;
    if (item.stock === 0) return `재고 없음 (현재 0개)`;
    if (qty > item.stock) return `재고 초과 — 현재 ${item.stock}개 / 요청 ${qty}개`;
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientName.trim()) { setError('거래처명을 입력해주세요.'); return; }
    const validItems = items.filter(i => i.product_name && i.quantity && i.unit_price);
    if (!validItems.length) { setError('품목을 1개 이상 입력해주세요.'); return; }

    // 재고 초과 확인
    for (const item of validItems) {
      if (item.stock !== undefined && (parseInt(item.quantity) || 0) > item.stock) {
        setError(`"${item.product_name}" 재고 부족 (현재 ${item.stock}개)`);
        return;
      }
    }

    setSaving(true); setError('');
    const res  = await authFetch('/api/shipments', {
      method: 'POST',
      body:   JSON.stringify({
        client_name:   clientName.trim(),
        shipment_date: shipmentDate,
        items:         validItems,
        note,
      }),
    });
    const data = await res.json();
    setSaving(false);
    if (!res.ok) { setError(data.error ?? '처리 실패'); return; }
    onSuccess(`상품출고 완료 (${data.shipment_no}) — 재고 자동 차감`);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-3 py-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">

        <div className="sticky top-0 bg-white border-b border-stone-100 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-bold text-lg text-stone-900">상품 출고 거래명세표</h2>
            <p className="text-xs text-stone-400 mt-0.5">품명 입력 시 재고 자동 검색 · 출고 후 재고 자동 차감</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700 text-2xl">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">

          {/* 거래처 */}
          <div className="border border-stone-200 rounded-xl p-4 bg-stone-50">
            <h3 className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-3">거래처 정보</h3>
            <div>
              <label className="block text-xs font-semibold text-stone-500 mb-1">거래처명 *</label>
              <input value={clientName} onChange={e => setClientName(e.target.value)} required
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                placeholder="거래처(구매처) 이름 입력" />
            </div>
          </div>

          {/* 출고일자 */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1.5">출고일자</label>
              <input type="date" value={shipmentDate} onChange={e => setShipmentDate(e.target.value)} required
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <div className="flex-1 text-right">
              <p className="text-xs text-stone-400 mb-1">명세표 번호</p>
              <p className="text-sm font-mono text-stone-600">SHP-{shipmentDate.replace(/-/g,'')}-자동생성</p>
            </div>
          </div>

          {/* 품목 테이블 */}
          <div>
            <h3 className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">
              출고 품목
              <span className="ml-2 text-stone-400 font-normal normal-case">품명 입력 시 재고 자동 검색</span>
            </h3>
            <div className="border border-stone-200 rounded-xl overflow-visible">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-stone-50 border-b border-stone-200">
                    <th className="text-left px-3 py-2.5 font-semibold text-stone-600 w-5/12">품명</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-stone-600 w-2/12">단가</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-stone-600 w-2/12">수량</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-stone-600 w-2/12">금액</th>
                    <th className="w-1/12"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => {
                    const amt  = (parseInt(item.quantity)||0) * (parseInt(item.unit_price)||0);
                    const warn = stockWarn(item);
                    return (
                      <tr key={idx} className="border-b border-stone-100 last:border-0">
                        <td className="px-2 py-1.5 relative">
                          <ProductSearch
                            value={item.product_name}
                            onSelect={p => onProductSelect(idx, p)}
                            onChange={name => setItem(idx, { product_name: name, product_id: undefined, stock: undefined })}
                            placeholder="품명 검색 또는 직접 입력"
                            showStock={true}
                            warnOnLow={true}
                          />
                          {/* 재고 초과 경고 */}
                          {warn && (
                            <p className="text-xs text-red-500 px-1">{warn}</p>
                          )}
                          {/* 현재 재고 표시 */}
                          {item.stock !== undefined && !warn && (
                            <p className="text-xs text-stone-400 px-1">현재 재고: {item.stock}개</p>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="number" value={item.unit_price}
                            onChange={e => setItem(idx, { unit_price: e.target.value })}
                            className="w-full border-0 bg-transparent focus:outline-none focus:bg-blue-50 rounded px-1 py-0.5 text-sm text-right"
                            placeholder="0" min="0" />
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="number" value={item.quantity}
                            onChange={e => setItem(idx, { quantity: e.target.value })}
                            className={`w-full border-0 bg-transparent focus:outline-none rounded px-1 py-0.5 text-sm text-right
                              ${warn ? 'text-red-600 focus:bg-red-50' : 'focus:bg-blue-50'}`}
                            placeholder="0" min="1"
                            max={item.stock ?? undefined} />
                        </td>
                        <td className="px-3 py-1.5 text-right text-stone-700 font-medium">
                          {amt > 0 ? amt.toLocaleString() : '-'}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <button type="button" onClick={() => removeItem(idx)}
                            className="text-stone-300 hover:text-red-400 text-lg leading-none">×</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-blue-50 border-t-2 border-blue-200">
                    <td colSpan={3} className="px-3 py-2.5 text-right font-bold text-stone-700">합계 금액</td>
                    <td className="px-3 py-2.5 text-right font-bold text-blue-700 text-base">{total.toLocaleString()}원</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <button type="button" onClick={() => setItems(prev => [...prev, emptyItem()])}
              className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
              <span className="text-lg leading-none">+</span> 품목 추가
            </button>
          </div>

          {/* 비고 */}
          <div>
            <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1.5">비고</label>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
              placeholder="배송 정보, 특이사항 등 (선택)" />
          </div>

          {error && <p className="text-red-500 text-sm bg-red-50 px-4 py-3 rounded-xl">{error}</p>}

          {/* 재고 자동 차감 안내 */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-700 flex items-start gap-2">
            <span className="text-base"></span>
            <p>출고 처리 후 품목별 재고가 <strong>자동으로 차감</strong>됩니다. 재고 부족 시 출고 처리가 불가합니다.</p>
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 border border-stone-200 py-3 rounded-xl text-sm hover:bg-stone-50 transition">취소</button>
            <button type="submit" disabled={saving}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-3 rounded-xl text-sm transition">
              {saving ? '처리 중...' : `상품 출고 처리 (${total.toLocaleString()}원)`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
